import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@mui/material';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters } from '@/lib/api';
import Markets from '@/constants/Markets';
import { mapConfigToOptions } from '@/lib/listOptions';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import IconButton from '@/components/IconButton/IconButton';
import EditPencilIcon from '@/assets/edit-pencil.svg?react';
import Button from '@/components/Button/Button';
import AddCircleIcon from '@/assets/add-circle.svg?react';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import QueryState from '@/components/QueryState/QueryState';
import CreateMarketModal from './CreateMarketModal';

type MarketItem = {
  id: number;
  name: string;
};

const MarketsAdminPage = () => {
  const navigate = useNavigate();
  const [createMarketModalOpen, setCreateMarketModalOpen] = useState(false);
  const hasCreatePermission = useHasUserPermissions(['markets.create']);
  const filtersQuery = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const { data, refetch } = filtersQuery;

  const marketItems = useMemo(() => {
    if (!data?.markets) return [];
    return mapConfigToOptions(Markets, data.markets, {
      id: 'market_id',
      name: 'market_name',
    }).map((item) => ({
      id: item.value as number,
      name: item.text as string,
    }));
  }, [data?.markets]);

  const columns: ColumnDef<MarketItem>[] = [
    {
      field: 'name',
      label: 'Market Name',
      width: '80%',
      render: (market) => (
        <MarketNameCell>
          {
            Markets[
              data?.markets?.find((m) => m.market_id === market.id)
                ?.market_name || ''
            ]?.icon
          }
          <MarketName>{market.name}</MarketName>
        </MarketNameCell>
      ),
    },
    {
      id: 'edit',
      label: <FormattedMessage id="edit" defaultMessage="Edit" />,
      width: '10%',
      sortable: false,
      render: (row) => (
        <IconButton
          onClick={() => navigate(`/admin/markets/${row.id}`)}
        >
          <EditPencilIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Root>
      <div className="page-width-wrapper">
        <Main className="wide-scrollbar">
          <Box
            sx={{ display: 'flex', width: '100%', marginTop: '40px' }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Title>
                <FormattedMessage
                  id="marketsAdmin"
                  defaultMessage="Markets Admin"
                />
              </Title>
            </Box>
            {hasCreatePermission ? (
              <Button
                variant="contained"
                onClick={() => setCreateMarketModalOpen(true)}
                disabled={!data}
                aria-label="Create Market Button"
              >
                <AddCircleIcon />
                <FormattedMessage
                  id="createMarket"
                  defaultMessage="Create Market"
                />
              </Button>
            ) : null}
          </Box>
          <QueryState
            query={filtersQuery}
            errorMessage={
              <FormattedMessage
                id="marketsAdminLoadError"
                defaultMessage="We couldn't load markets. Check your connection and try again."
              />
            }
          >
            <StyledTable
              rows={marketItems}
              columns={columns}
              emptyMessage={
                <FormattedMessage
                  id="marketsAdminEmpty"
                  defaultMessage="No markets yet."
                />
              }
            />
          </QueryState>
        </Main>
        <CreateMarketModal
          open={createMarketModalOpen}
          onClose={() => setCreateMarketModalOpen(false)}
          onSave={() => {
            refetch();
            setCreateMarketModalOpen(false);
          }}
        />
      </div>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  height: '100%',
  background: '#F9FAFB',

  '> *': {
    maxHeight: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
  },
});

const Main = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`,
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  marginBottom: 8,
}));

const StyledTable = styled(Table<MarketItem>)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const MarketNameCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 12,
});

const MarketName = styled('span')({
  fontSize: '14px',
  fontWeight: 400,
});

export default MarketsAdminPage;
